/*###########################################################################
 // Decks island flyout — paste TCG Live export → create deck
 //#########################################################################*/

import { showSiteToast } from './_siteToast'

function config() {
  const el = document.getElementById('deck-import-flyout-config')
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

function navigateTo(path) {
  const swup = window.swup
  if (swup?.navigate) {
    swup.navigate(path)
    return
  }
  window.location.href = path
}

export function initDeckImportFlyout() {
  const form = document.querySelector('[data-deck-import-form]')
  if (!form || form.dataset.bound) return
  form.dataset.bound = '1'

  const flyout = form.closest('[data-nav-flyout]')
  const status = form.querySelector('[data-deck-import-status]')
  const input = form.querySelector('[data-deck-import-input]')

  // Keep the hover flyout open while the paste field is focused.
  if (flyout && input) {
    input.addEventListener('focus', () => {
      flyout.classList.add('is-open')
      flyout.querySelector('[data-flyout-trigger]')?.setAttribute('aria-expanded', 'true')
    })
  }

  let importing = false

  async function importExport(exportText) {
    const cfg = config()
    if (!cfg?.importUrl || importing) return

    const text = String(exportText || '').trim()
    if (!text) {
      showSiteToast('Paste a TCG Live deck export first')
      return
    }

    importing = true
    if (input) input.disabled = true
    if (status) {
      status.hidden = false
      status.textContent = 'Building deck…'
      status.classList.remove('is-error')
    }

    try {
      const payload = new FormData()
      payload.append(cfg.csrfName, cfg.csrfToken)
      payload.append('exportText', text)
      payload.append('format', 'standard')

      const res = await fetch(cfg.importUrl, {
        method: 'POST',
        body: payload,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.success === false) {
        throw new Error(data.error || `Import failed (${res.status})`)
      }

      if (input) input.value = ''
      if (status) status.hidden = true

      const unmatched = Array.isArray(data.unmatched) ? data.unmatched.length : 0
      if (unmatched) {
        showSiteToast(`Deck built — ${unmatched} unmatched line${unmatched === 1 ? '' : 's'}`)
      } else {
        showSiteToast('Deck imported')
      }

      // Fire before swup navigates so the header (outside the swap root) still
      // pops open and ticks the walkthrough item like other onboarding steps.
      if (data.justCompletedWalkthroughTask === 'importedFirstDeck') {
        window.dispatchEvent(
          new CustomEvent('app:walkthrough-task', { detail: { task: 'importedFirstDeck' } }),
        )
      }

      navigateTo(data.editUrl || data.deck?.editUrl || cfg.decksUrl || '/account/decks')
    } catch (err) {
      const message = err.message || 'Import failed'
      if (status) {
        status.hidden = false
        status.textContent = message
        status.classList.add('is-error')
      }
      showSiteToast(message)
    } finally {
      importing = false
      if (input) input.disabled = false
    }
  }

  // Paste into the one-line field → auto-build the deck.
  if (input) {
    input.addEventListener('paste', (e) => {
      const pasted = e.clipboardData?.getData('text') || ''
      if (!pasted.trim()) return
      e.preventDefault()
      input.value = pasted.trim().split(/\r?\n/)[0] || pasted.trim()
      // Prefer the full clipboard payload for parsing (multi-line export).
      importExport(pasted)
    })
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    importExport(input?.value || '')
  })
}
